import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CtAttributesService } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { CtMetadata } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-metadata';
import { CTResultsService } from '@cloud-twin-v2/cloud-twin-shared/core/results/ct-results.service';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import { CtResult } from '@cloud-twin-v2/ct-map/models/ct-result';
import { NetworkElementResult } from '@cloud-twin-v2/ct-map/models/network-element-result';
import {
  CtWorkspaceDto,
  MeasurementDto,
  WorkspaceConnectSettingDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { TelemetryPointsService } from '@cloud-twin-v2/telemetry/services/telemetry-points.service';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { debounceTime, forkJoin, map, Observable, of, startWith, take } from 'rxjs';
import { CtNetworkElementFilters } from '../models/ct-network-element-filters';
import { SearchResultAttribute } from '../models/search-result-attribute';
import { SearchResultGroup } from '../models/search-result-group';
import { SearchResultGroupType } from '../models/search-result-group-type';
import { SearchResultItem } from '../models/search-result-item';
import { SearchResultItemType } from '../models/search-result-item-type';

const COMPONENT_SELECTOR = 'ct-attributes-selector';

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './ct-attributes-selector.component.html',
  styleUrl: './ct-attributes-selector.component.scss',
  standalone: false,
})
export class CtAttributesSelectorComponent {
  private _filters: CtNetworkElementFilters;
  get filters(): CtNetworkElementFilters {
    return this._filters;
  }
  @Input() set filters(value: CtNetworkElementFilters) {
    this._filters = value;
    this.applySearchAndFilters();
  }

  private _existingNetworkElements: string[] = [];
  @Input()
  set existingNetworkElements(value: string[]) {
    this._existingNetworkElements = value || [];
    this.onExistingNetworkElementsChange();
  }
  get existingNetworkElements(): string[] {
    return this._existingNetworkElements;
  }

  @Output() addAttributesToCart = new EventEmitter<SearchResultAttribute[]>();
  @Output() addMeasurementToCart = new EventEmitter<SearchResultItem>();

  searchForm: FormGroup;
  selectAllAttributes = false;
  expandedGroups: { [key: string]: boolean } = {};
  searchGroups: SearchResultGroup[] = [];
  selectedSearchItem: SearchResultItem;
  attributesExpanded = true;
  attributeResults: SearchResultAttribute[] = [];
  metadata: CtMetadata = {};

  readonly SearchResultItemType = SearchResultItemType;
  readonly itemSize = 24;
  readonly T_SCOPE = `${AppModules.RightPanel}.${COMPONENT_SELECTOR}`;
  private _searchText: string;
  private _selectedWorkspace: CtWorkspaceDto;
  private readonly _fb = inject(FormBuilder);
  private readonly _ctResultsService = inject(CTResultsService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _selectedWorkspaceService = inject(SelectedWorkspaceService);
  private readonly _telemetryPointsService = inject(TelemetryPointsService);
  private readonly _localization = inject(LocalizationHelperService);
  private readonly _ctAttributeService = inject(CtAttributesService);

  private readonly _searchDebounceTime = 300;

  constructor() {
    this.searchForm = this._fb.group({
      searchText: [],
    });
  }

  ngOnInit(): void {
    this._selectedWorkspace$.subscribe({
      next: (selectedWorkspace) => {
        this._selectedWorkspace = selectedWorkspace;

        this.searchForm.controls.searchText.valueChanges
          .pipe(
            takeUntilDestroyed(this._destroyRef),
            startWith(''),
            debounceTime(this._searchDebounceTime)
          )
          .subscribe({
            next: (searchText) => {
              this._searchText = searchText?.toLowerCase();
              this.applySearchAndFilters();
            },
          });
      },
    });
    this._ctAttributeService.metadata$.pipe(take(1)).subscribe((metadata) => {
      this.metadata = metadata;
    });
  }

  private get _selectedWorkspace$() {
    return this._selectedWorkspaceService.selectedWorkspace$.pipe(
      takeUntilDestroyed(this._destroyRef)
    );
  }

  private applySearchAndFilters(): void {
    if (!this._selectedWorkspace) {
      return;
    }

    forkJoin([this.performNetworkElementsSearch(), this.performMeasurementsSearch()]).subscribe({
      next: ([neGroups, measurementGroups]) => {
        this.searchGroups = neGroups.concat(measurementGroups);
      },
    });
  }

  toggleExpandedSection(sectionId: string): void {
    this.expandedGroups[sectionId] = !this.expandedGroups[sectionId];
  }

  toggleSelectAll(): void {
    this.attributeResults.forEach((result) => {
      result.selected = this.selectAllAttributes;
    });
  }

  setSelectedSearchItem(item: SearchResultItem): void {
    this.selectedSearchItem = item;
    this.loadSelectedItemAttributes();
  }

  onAddAttributesToCart(): void {
    this.addAttributesToCart.emit(this.selectedAttributes);
  }

  onAddMeasurementToCart(item: SearchResultItem): void {
    this.addMeasurementToCart.emit(item);
  }

  get selectedAttributes(): SearchResultAttribute[] {
    return this.attributeResults.filter((attribute) => attribute.selected);
  }

  isAttributeInCart(attr: SearchResultAttribute): boolean {
    if (!this.existingNetworkElements) return false;
    return this.existingNetworkElements.includes(attr.cartTitle);
  }

  isAttributeChecked(attr: SearchResultAttribute): boolean {
    if (this.isAttributeInCart(attr)) {
      return false;
    }
    return attr.selected;
  }

  onCheckboxChange(attr: SearchResultAttribute, value: boolean): void {
    if (!this.isAttributeInCart(attr)) {
      attr.selected = value;
    }
  }

  onExistingNetworkElementsChange(): void {
    if (this.attributeResults && this.existingNetworkElements) {
      this.attributeResults.forEach((attr) => {
        if (this.existingNetworkElements.includes(attr.cartTitle)) {
          attr.selected = false;
        }
      });
    }
  }

  private loadSelectedItemAttributes(): void {
    this.attributeResults = this.selectedSearchItem.attributes;
  }

  private performNetworkElementsSearch(): Observable<SearchResultGroup[]> {
    return this._ctResultsService.results$.pipe(
      take(1),
      map((results) => {
        const filteredResults = this.filterResults(results);
        const filteredGroups: SearchResultGroup[] = [];
        const groupType = SearchResultGroupType.NetworkElement;

        for (let result of filteredResults) {
          // Search results are grouped by model version.
          const settingsItem = this.getModelFromScenario(result.scenarioId);
          const searchResultItems = this.buildNetworkElementResults(
            result.networkElements,
            settingsItem
          );

          let group = filteredGroups.find((group) => group.groupId === settingsItem.instanceId);
          if (!group) {
            group = {
              groupId: `${settingsItem.instanceId}-${settingsItem.snapshotId}-ne`,
              label$: this.getNEGroupLabel$(settingsItem),
              items: searchResultItems,
              groupType,
            };
            filteredGroups.push(group);
          } else {
            group.items = group.items.concat(searchResultItems);
          }

          this.expandedGroups[group.groupId] = true;
        }

        return filteredGroups;
      })
    );
  }

  private performMeasurementsSearch(): Observable<SearchResultGroup[]> {
    return forkJoin([
      this._telemetryPointsService.measurements$.pipe(take(1)),
      this.getScenariosByModelVersion().pipe(take(1)),
    ]).pipe(
      map(([measurements, scenariosByModelVersion]) => {
        const filteredMeasurements = this.filterMeasurements(measurements);
        const filteredGroups: SearchResultGroup[] = [];
        for (let [modelVersionId, settingsItems] of scenariosByModelVersion.entries()) {
          const searchResultItems = this.buildMeasurementResults(
            filteredMeasurements,
            settingsItems
          );
          const settingsItem = settingsItems.length ? settingsItems[0] : null;
          const groupType = SearchResultGroupType.Measurement;

          const group: SearchResultGroup = {
            groupId: `${modelVersionId}-measurement`,
            label$: this.getMeasurementGroupLabel$(settingsItem),
            items: searchResultItems,
            groupType,
          };

          filteredGroups.push(group);
          this.expandedGroups[group.groupId] = false;
        }

        return filteredGroups;
      })
    );
  }

  private getNEGroupLabel$(settingsItem: WorkspaceConnectSettingDto): Observable<string> {
    return this._localization.get(`${this.T_SCOPE}.groups.network-elements`, {
      modelName: settingsItem.instanceName,
      modelVersionName: settingsItem.snapshotName,
    });
  }

  private getMeasurementGroupLabel$(settingsItem: WorkspaceConnectSettingDto): Observable<string> {
    return this._localization.get(`${this.T_SCOPE}.groups.measurements`, {
      modelName: settingsItem.instanceName,
      modelVersionName: settingsItem.snapshotName,
    });
  }

  private buildMeasurementResults(
    measurements: MeasurementDto[],
    settingsItems: WorkspaceConnectSettingDto[]
  ): SearchResultItem[] {
    const searchResultItems: SearchResultItem[] = [];

    for (let measurement of measurements) {
      const linkedSettingsItem = settingsItems.find(
        (settingItem) => settingItem.scenarioId === measurement.scenarioId
      );

      if (linkedSettingsItem) {
        searchResultItems.push({
          resultId: measurement.pointId, // TODO: Confirm this
          label$: of(measurement.pointId),
          breadcrumbs$: this.getMeasurementItemBreadcrumb$(linkedSettingsItem),
          attributes: null,
          itemType: SearchResultItemType.Measurement,
          params: {
            measurement,
            settingsItem: linkedSettingsItem,
          },
        });
      }
    }

    return searchResultItems;
  }

  private getMeasurementItemBreadcrumb$(
    settingsItem: WorkspaceConnectSettingDto
  ): Observable<string> {
    return this._localization.get(`${this.T_SCOPE}.breadcrumbs.measurement`, {
      modelName: settingsItem.instanceName,
      modelVersionName: settingsItem.snapshotName,
      physType: null,
    });
  }

  private getScenariosByModelVersion(): Observable<Map<string, WorkspaceConnectSettingDto[]>> {
    return this._selectedWorkspace$.pipe(
      take(1),
      map((workspace) => {
        const scenarioIds = this.filters?.scenarioIds;
        let items = workspace.workspaceConnectSettings;

        if (scenarioIds?.length) {
          items = items.filter((item) => scenarioIds.includes(item.scenarioId));
        }

        const groups = globalUtilsHelper.groupByFnHash(items, (item) => item.snapshotId);
        return groups;
      })
    );
  }

  private filterResults(results: CtResult[]): CtResult[] {
    let filteredResults = globalUtilsHelper.clone(results, true);

    if (this.filters?.scenarioIds?.length) {
      filteredResults = filteredResults.filter((result) =>
        this.filters.scenarioIds.includes(result.scenarioId)
      );
    }

    if (this.filters?.networkElementTypeIds?.length) {
      filteredResults = filteredResults.filter((result) => {
        const filteredNetworkElements = result.networkElements.filter((ne) =>
          this.filters.networkElementTypeIds.includes(ne.networkElementTypeId)
        );
        if (filteredNetworkElements.length) {
          result.networkElements = filteredNetworkElements;
          return true;
        }
        return false;
      });
    }

    return filteredResults;
  }

  private filterMeasurements(measurements: MeasurementDto[]): MeasurementDto[] {
    let filteredItems = globalUtilsHelper.clone(measurements, true);
    const scenarioIds = this.filters?.scenarioIds;
    const networkElementTypeIds = this.filters?.networkElementTypeIds;

    filteredItems = filteredItems.filter((item) => {
      const filteredBySearchText =
        !this._searchText ||
        this._searchText === '' ||
        item.pointId.toLowerCase().includes(this._searchText);
      if (!filteredBySearchText) {
        return;
      }

      // It has to exist at leat one point that associates the measurement to the scenario / net.

      const filteredByScenarios =
        !scenarioIds?.length ||
        item.pointConfigurations.some((point) => scenarioIds.includes(point.scenario));
      if (!filteredByScenarios) {
        return;
      }

      const filteredByNets =
        !networkElementTypeIds?.length ||
        item.pointConfigurations.some((point) =>
          networkElementTypeIds.includes(point.networkElementType)
        );
      return filteredByNets;
    });

    return filteredItems;
  }

  private buildNetworkElementResults(
    networkElements: NetworkElementResult[],
    settingsItem: WorkspaceConnectSettingDto
  ): SearchResultItem[] {
    const items: SearchResultItem[] = this.filterNetworkElements(networkElements).map(
      (element) =>
        ({
          resultId: element.networkElementId,
          label$: of(element.networkElementName),
          breadcrumbs$: of(
            [
              settingsItem.snapshotName,
              settingsItem.scenarioName,
              element.networkElementTypeId,
            ].join(' / ')
          ),
          attributes: this.buildSearchItemAttributes(element, settingsItem),
          itemType: SearchResultItemType.AttributeResult,
        } as SearchResultItem)
    );

    return items;
  }

  private buildSearchItemAttributes(
    element: NetworkElementResult,
    settingsItem: WorkspaceConnectSettingDto
  ): SearchResultAttribute[] {
    const elementToMap = element.attributes.map(
      (attribute) =>
        ({
          attributeId: attribute.name,
          attributeName: attribute.name,
          breadcrumbs: settingsItem.scenarioName,
          cartTitle: [element.networkElementName, attribute.name].join(' - '),
          params: {
            element,
            settingsItem,
          },
        } as SearchResultAttribute)
    );

    return this.buildAttributeAdditionalProperties(elementToMap);
  }

  private buildAttributeAdditionalProperties(
    attributes: SearchResultAttribute[]
  ): SearchResultAttribute[] {
    const allAttributes: any[] = [];

    for (const scenarioId in this.metadata) {
      const categoriesArr = this.metadata[scenarioId];

      categoriesArr.forEach((categoryObj: any) => {
        categoryObj.categories.forEach((category: any) => {
          allAttributes.push(...category.attributes);
        });
      });
    }

    attributes.forEach((el) => {
      const found = allAttributes.find((attr) => attr.name === el.attributeName);
      if (found && found.alias) {
        el.attributeAlias = found.alias;
        el.units = found.units;
        el.physType = found.physType;
      }
    });
    return attributes;
  }

  private getModelFromScenario = (scenarioId: string): WorkspaceConnectSettingDto => {
    const result = this._selectedWorkspace.workspaceConnectSettings.find(
      (item) => item.scenarioId === scenarioId
    );
    return result;
  };

  private filterNetworkElements = (networkElements: NetworkElementResult[]) =>
    networkElements.filter((element) =>
      element.networkElementName.toLowerCase().includes(this._searchText)
    );
}
